import React from 'react';
import _ from 'lodash';
import Isvg from 'react-inlinesvg';
import { Badge } from 'reactstrap';
import Header from 'app/components/layout/Header';
import SectionGroup from 'app/components/general/SectionGroup';
import ConnectionGroup from 'app/components/general/ConnectionGroup';
import StatusBadge from 'app/components/general/StatusBadge';
import CopyCaption from 'app/components/general/CopyCaption';

import { simpleDateTwo, parseAmount } from 'app/utils/parsers';
import { IPerson } from 'app/ducks/people/types';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PaymentCollectionsTableCustomers from 'app/components/payments/PaymentCollectionsTableCustomers';

import * as peopleActions from 'app/ducks/people/actions';
import * as paymentCollectionActions from 'app/ducks/paymentCollections/actions';

import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as peopleSelectors from 'app/ducks/people/selectors';
import * as paymentCollectionsSelectors from 'app/ducks/paymentCollections/selectors';

import CompetitionIcon from 'app/images/icons/organisation.svg';

function selector(state) {
  return {
    people: peopleSelectors.getPeople(state),
    paymentCollections: paymentCollectionsSelectors.getPaymentCollections(state),
    appReady: loadingSelectors.appReady(state),
  };
}

const actionCreators = _.merge(
  peopleActions,
  paymentCollectionActions,
);

interface ICustomerDetailProps {
  appReady: boolean;
  actions: any;
  match: any;
  people: any;
  paymentCollections: any;
  backendErrors: any;
  errors: any;
  isLoading: boolean;
}
@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class CustomerDetail extends React.Component<ICustomerDetailProps> {
  static defaultProps: ICustomerDetailProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    people: undefined,
    paymentCollections: undefined,
    backendErrors: undefined,
    isLoading: false,
    errors: undefined,
  };

  componentDidMount = (): void => {
    this.props.actions.getPerson(this.props.match.params.customerId);
  };

  statusBadges = (owingNumber, overdueNumber): React.ReactNode[] => {
    const statuses = [];

    if (owingNumber > 0) {
      statuses.push(
        <StatusBadge
          paymentText="owing"
          numberOfObjects={owingNumber}
        />,
      );
    }
    if (overdueNumber > 0) {
      statuses.push(
        <StatusBadge
          paymentText="overdue"
          numberOfObjects={overdueNumber}
        />,
      );
    }

    return statuses;
  };

  render(): React.ReactNode {
    const person = this.props.people.currentPerson;
    const isLoading = this.props.people.isLoading && person?.id !== this.props.match.params.customerId;
    return (
      <div>
        <div className="row">
          <div className="col">
            <Header
              title={person?.fullName}
              isLoading={isLoading}
              badge={
                this.statusBadges(person?.pendingPaymentCollectionsTotalCount, person?.overduePaymentCollectionsTotalCount)
              }
              descriptors={
                [
                  {
                    icon: <Isvg src={CompetitionIcon} />,
                    descriptor: person?.competitionName,
                  },
                ]
              }
              metadata={[
                {
                  title: 'Since',
                  value: person?.createdAt !== undefined ? simpleDateTwo(person?.createdAt) : '',
                },
                {
                  title: 'Teams',
                  value: person?.numberOfGroups?.toString(),
                },
                {
                  title: 'Paid-to-date',
                  value: parseAmount(person?.paidPaymentCollectionsTotalAmount),
                },
                {
                  title: 'Owing',
                  value: parseAmount(person?.pendingPaymentCollectionsTotalAmount),
                },
              ]}
            />
          </div>
        </div>
        <div className="main-content">
          <div className="row">
            <div className="col col-12 col-lg-8 col-xlg-6">
              <SectionGroup title="Details" isLoading={isLoading}>
                <div className="row">
                  <div className="col col-4 text-grey">
                    Full Name
                  </div>
                  <div className="col">
                    {person?.fullName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Email
                  </div>
                  <div className="col">
                    {person?.email}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Mobile
                  </div>
                  <div className="col">
                    {person?.mobile}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Unique Key
                  </div>
                  <div className="col">
                    {!_.isEmpty(person?.uniqueCode) && <CopyCaption text={person?.uniqueCode} />}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Competition/s
                  </div>
                  <div className="col">
                    {person?.competitionName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Section/s
                  </div>
                  <div className="col">
                    {person?.sectionName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Season/s
                  </div>
                  <div className="col">
                    {person?.seasonName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Internal Identifier
                  </div>
                  <div className="col">
                    <CopyCaption text={person?.id} />
                  </div>
                </div>
              </SectionGroup>
            </div>
          </div>
          <div className="row with-margin-top-extra">
            <div className="col col-12 col-lg-8 col-xlg-6">
              <ConnectionGroup
                title="Connections"
                isLoading={isLoading}
                items={
                  [
                    {
                      type: 'team',
                      title: 'Teams',
                      links: _.map(person?.groups, (group) => ({
                        name: group.name,
                        id: group.id,
                      })),
                    },
                    {
                      type: 'competition',
                      title: 'Competitions',
                      links: _.map(person?.competitions, (competition) => ({
                        name: competition.name,
                        id: competition.id,
                      })),
                    },
                  ]
                }
              />
            </div>
          </div>
          <div className="row with-margin-top-extra">
            <div className="col">
              <PaymentCollectionsTableCustomers
                {...this.props.paymentCollections}
                appReady={this.props.appReady}
                actions={this.props.actions}
                headerTitle="Payment Requests"
                saveFilters={false}
                hideButtons
                isLoading={this.props.paymentCollections.isLoading}
                persistentFilterConfig={{
                  personId: this.props.match.params.customerId,
                }}
                selectPaymentCollection={this.props.actions.selectPaymentCollection}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
